import { useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Trash2,
  X,
} from 'lucide-react';
import {
  useQuotes,
  useCreateQuote,
  useUpdateQuote,
  useDeleteQuote,
  useSendQuote,
  useAcceptQuote,
  useDeclineQuote,
  type QuoteFilters,
  type Quote,
  type QuoteItem,
  type QuoteStatus,
} from '../hooks/useQuotes';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface QuotesViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusLabels: Record<QuoteStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'gray' },
  sent: { label: 'Sent', color: 'blue' },
  accepted: { label: 'Accepted', color: 'green' },
  declined: { label: 'Declined', color: 'red' },
  expired: { label: 'Expired', color: 'orange' },
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const emptyLineItem = (): Partial<QuoteItem> => ({
  productName: '',
  premium: 0,
  deductible: undefined,
  coverageLimit: undefined,
  quantity: 1,
});

export function QuotesView({ onNavigate }: QuotesViewProps) {
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | ''>('');
  const [searchText, setSearchText] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);

  // Form state
  const [formData, setFormData] = useState({ dealId: '', terms: '', notes: '' });
  const [lineItems, setLineItems] = useState<Partial<QuoteItem>[]>([emptyLineItem()]);

  const filters: QuoteFilters = {
    status: statusFilter || undefined,
    search: searchText || undefined,
    limit: 100,
  };

  const { data, isLoading, error } = useQuotes(filters);
  const createQuote = useCreateQuote();
  const deleteQuote = useDeleteQuote();
  const sendQuote = useSendQuote();
  const acceptQuote = useAcceptQuote();
  const declineQuote = useDeclineQuote();

  const quotes = data?.quotes ?? [];

  function addLineItem() {
    setLineItems((prev) => [...prev, emptyLineItem()]);
  }

  function removeLineItem(index: number) {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  }

  function updateLineItem(index: number, field: string, value: string | number) {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  }

  function handleCreate() {
    const items = lineItems
      .filter((item) => item.productName)
      .map((item) => ({
        ...item,
        premium: Number(item.premium) || 0,
        quantity: Number(item.quantity) || 1,
        deductible: item.deductible ? Number(item.deductible) : undefined,
        coverageLimit: item.coverageLimit ? Number(item.coverageLimit) : undefined,
      }));

    createQuote.mutate(
      {
        dealId: formData.dealId || undefined,
        terms: formData.terms || undefined,
        notes: formData.notes || undefined,
        items: items as QuoteItem[],
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setFormData({ dealId: '', terms: '', notes: '' });
          setLineItems([emptyLineItem()]);
        },
      },
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Quotes</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} quote${data.total !== 1 ? 's' : ''}` : 'Manage quotes'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          New Quote
        </button>
      </div>

      {/* Filters */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-surface-200 p-0.5">
            {(['' as const, 'draft', 'sent', 'accepted', 'declined'] as const).map((s) => (
              <button
                key={s}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                  statusFilter === s ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:text-slate-900',
                )}
                onClick={() => setStatusFilter(s)}
              >
                {s ? statusLabels[s].label : 'All'}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search quotes..."
              className="input pl-9"
            />
          </form>
        </div>
      </div>

      <div className="flex">
        {/* Table */}
        <div className={cn('flex-1 p-6', selectedQuote && 'pr-0')}>
          {error ? (
            <div className="card p-8 text-center text-sm text-red-600">Failed to load quotes.</div>
          ) : isLoading ? (
            <div className="card">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-b border-surface-100 px-4 py-3 last:border-0">
                  <div className="h-4 w-32 animate-pulse rounded bg-surface-200" />
                  <div className="flex-1" />
                  <div className="h-4 w-20 animate-pulse rounded bg-surface-200" />
                </div>
              ))}
            </div>
          ) : quotes.length > 0 ? (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-200 bg-surface-50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Quote #</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Deal</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Organization</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Premium</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Valid Until</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {quotes.map((quote) => {
                    const sCfg = statusLabels[quote.status];
                    return (
                      <tr
                        key={quote.id}
                        className={cn('hover:bg-surface-50 cursor-pointer', selectedQuote?.id === quote.id && 'bg-brand-50')}
                        onClick={() => setSelectedQuote(quote)}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-brand-600">{quote.quoteNumber}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{quote.dealName ?? '--'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{quote.organizationName ?? '--'}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge color={sCfg.color} size="sm">{sCfg.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">{formatCurrency(quote.totalPremium)}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{quote.validUntil ? new Date(quote.validUntil).toLocaleDateString() : '--'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            {quote.status === 'draft' && (
                              <button className="btn-secondary btn-sm" onClick={() => sendQuote.mutate(quote.id)} disabled={sendQuote.isPending} title="Send">
                                <Send className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {quote.status === 'sent' && (
                              <>
                                <button className="btn-secondary btn-sm text-green-600" onClick={() => acceptQuote.mutate(quote.id)} disabled={acceptQuote.isPending} title="Accept">
                                  <CheckCircle className="h-3.5 w-3.5" />
                                </button>
                                <button className="btn-secondary btn-sm text-red-600" onClick={() => declineQuote.mutate({ id: quote.id })} disabled={declineQuote.isPending} title="Decline">
                                  <XCircle className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                            {quote.status === 'draft' && (
                              <button className="btn-secondary btn-sm text-red-600" onClick={() => deleteQuote.mutate(quote.id)} title="Delete">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={FileText}
              title="No quotes yet"
              description="Create a quote to send to clients."
              actionLabel="New Quote"
              onAction={() => setShowForm(true)}
            />
          )}
        </div>

        {/* Quote Detail Panel */}
        {selectedQuote && (
          <div className="w-96 border-l border-surface-200 bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Quote {selectedQuote.quoteNumber}</h2>
              <button onClick={() => setSelectedQuote(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-slate-500">Status</p>
                <Badge color={statusLabels[selectedQuote.status].color}>{statusLabels[selectedQuote.status].label}</Badge>
              </div>
              {selectedQuote.dealName && (
                <div>
                  <p className="text-xs font-medium text-slate-500">Deal</p>
                  <p className="text-sm text-slate-900">{selectedQuote.dealName}</p>
                </div>
              )}
              {selectedQuote.organizationName && (
                <div>
                  <p className="text-xs font-medium text-slate-500">Organization</p>
                  <p className="text-sm text-slate-900">{selectedQuote.organizationName}</p>
                </div>
              )}

              {/* Line Items */}
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Line Items</p>
                <div className="rounded-lg border border-surface-200 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-surface-50">
                        <th className="px-3 py-1.5 text-left text-2xs font-semibold text-slate-500">Product</th>
                        <th className="px-3 py-1.5 text-right text-2xs font-semibold text-slate-500">Premium</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-100">
                      {selectedQuote.items.map((item, i) => (
                        <tr key={item.id ?? i}>
                          <td className="px-3 py-2 text-xs text-slate-900">{item.productName}</td>
                          <td className="px-3 py-2 text-xs text-right text-slate-900">{formatCurrency(item.premium)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 flex justify-between text-sm font-semibold text-slate-900">
                  <span>Total</span>
                  <span>{formatCurrency(selectedQuote.totalPremium)}</span>
                </div>
              </div>

              {selectedQuote.terms && (
                <div>
                  <p className="text-xs font-medium text-slate-500">Terms</p>
                  <p className="text-sm text-slate-600">{selectedQuote.terms}</p>
                </div>
              )}
              {selectedQuote.notes && (
                <div>
                  <p className="text-xs font-medium text-slate-500">Notes</p>
                  <p className="text-sm text-slate-600">{selectedQuote.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Quote Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">New Quote</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label mb-1">Deal ID (optional)</label>
                <input className="input" value={formData.dealId} onChange={(e) => setFormData((f) => ({ ...f, dealId: e.target.value }))} placeholder="Link to a deal" />
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Line Items</label>
                  <button className="btn-secondary btn-sm" onClick={addLineItem}><Plus className="h-3 w-3" /> Add</button>
                </div>
                <div className="space-y-2">
                  {lineItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-lg border border-surface-200 p-2">
                      <input className="input flex-1" placeholder="Product name" value={item.productName ?? ''} onChange={(e) => updateLineItem(i, 'productName', e.target.value)} />
                      <input className="input w-28" type="number" placeholder="Premium" value={item.premium ?? ''} onChange={(e) => updateLineItem(i, 'premium', e.target.value)} />
                      <input className="input w-24" type="number" placeholder="Deductible" value={item.deductible ?? ''} onChange={(e) => updateLineItem(i, 'deductible', e.target.value)} />
                      <input className="input w-24" type="number" placeholder="Limit" value={item.coverageLimit ?? ''} onChange={(e) => updateLineItem(i, 'coverageLimit', e.target.value)} />
                      {lineItems.length > 1 && (
                        <button className="text-red-400 hover:text-red-600" onClick={() => removeLineItem(i)}>
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="label mb-1">Terms</label>
                <textarea className="input" rows={2} value={formData.terms} onChange={(e) => setFormData((f) => ({ ...f, terms: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Notes</label>
                <textarea className="input" rows={2} value={formData.notes} onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate} disabled={createQuote.isPending}>
                {createQuote.isPending ? 'Creating...' : 'Create Quote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
